import {PolymerElement} from '@polymer/polymer/polymer-element.js';
import {TranslationsUtil} from '@nuskin/ns-util';
import {RunConfigService} from '@nuskin/ns-util';
import {html} from '@polymer/polymer/lib/utils/html-tag.js';
import {Translations} from './translations.js';
import template from './ns-dropdown.html';
import '../ns-form/ns-form.js';

/**
 * The ns-dropdown should be used for all dropdowns at nuskin. It can do lots of cool stuff
 *
 * <h1>Dropdown</h1>
 * <demo-snippet class="demoSnippet">
 *   <template>
 *     <ns-dropdown label="Demo Dropdown" options='["Item 1", "Item 2", "Item 3"]'></ns-dropdown>
 *   </template>
 * </demo-snippet>
 *
 */
export class NsDropdown extends PolymerElement {

    static get is() {
        return "ns-dropdown";
    }

    static get template() {
        let htmlElement = document.createElement('TEMPLATE');
        htmlElement.innerHTML = template;
        return htmlElement;
    }

    ready() {
        super.ready();
        this._initialize();
    }

    constructor() {
        super();
    }

    connectedCallback() {
        super.connectedCallback();
    }

    static get properties() {
        return {
            active: {
                type: String,
                value: "inactive"
            },
            _defaultDropdownMinWidth: {
                type: Number,
                value: 175
            },
            dontTrim: {
                type: Boolean,
                value: false
            },
            dropdownId: {
                type: String,
                value: 'ns-dropdown'
            },
            dynamicWidth: {
                type: Boolean,
                value: false
            },
            emptySelect: {
                type: Boolean,
                value: false
            },
            flairArray: {
                type: Array,
                value: []
            },
            flairPath: {
                type: String,
                value: 'flair'
            },
            idArray: {
                type: Array,
                value: []
            },
            idPath: {
                type: String,
                value: "id"
            },
            invalid: {
                type: Boolean,
                notify: true,
                value: false,
                observer: 'showErrors'
            },
            globalSearch: {
                type: Boolean,
                value: false
            },
            label: {
                type: String,
                value: ''
            },
            multiselect: {
                type: Boolean,
                value: false
            },
            name: {
                type: String
            },
            options: {
                type: Array,
                value: [],
                observer: "_setOptions"
            },
            optionsObjectArray: {
                type: Array,
                value: [],
                observer: "_initialize"
            },
            required: {
                type: Boolean
            },
            requiredMessage: {
                type: String
            },
            selected: {
                type: String,
                notify: true,
                observer: '_setOptions'
            },
            subtitles: {
                type: Array,
                value: []
            },
            subtitlePath: {
                type: String,
                value: "subtitle"
            },
            title: String,
            titlePath: {
                type: String,
                value: "title"
            },
            trimLength: {
                type: Number,
                value: 15
            },
            useSubtitle: {
                type: Boolean,
                value: false
            },
            hideDropdown: {
                type: Boolean,
                value: false
            },
            showDropdownLayout: {
                type: Boolean,
                value: true
            },
            showSingleItemLayout: {
                type: Boolean,
                value: false
            },
            /**
             * The display name of the currently selected dropdown item.
             */
            currentSelection: {
                type: String,
                notify: true,
                observer: '_trimCurrentSelection'
            },
            /**
             * Show a unique image or color in front of a dropdown option. Does not work with multiselect.
             */
            useFlair: {
                type: Boolean,
                value: false
            },
            /**
             * Show a black bar on the side of a dropdown option.
             */
            useSelectionHighlight: {
                type: Boolean,
                value: false
            },
            /**
             * Page upon which this component appears. Used for translations.
             */
            webPageName: {
                type: String
            },
            /**
             * Translations from the server. Triggers the translation assignment process.
             * If undefined and shouldAwaitTranslations=false, this component will fetch its own strings.
             */
            serverTranslations: {
                type: Object,
                observer: '_assignTranslations'
            },
            /**
             * If false, it will start the translation assignment process without waiting.
             * If true, it will wait for serverTranslations to be set before starting the translation assignment process.
             * If serverTranslations is defined already, the component will behave as if this property is true.
             */
            shouldAwaitTranslations: {
                type: Boolean,
                value: false
            },
            /**
             * Boolean to hide label above the dropdown.
             */
            hideLabel: {
                type: Boolean,
                value: false
            },
            /**
             * Dirty flag for this component
             */
            dirty: {
                type: Boolean,
                vale: false,
                notify: true,
                reflectToAttribute: true
            }
        };
    }

    _initialize() {
        // Don't do anything until we have the options list
        if (!this.TRANSLATION_HELPER) {
            this._localize();
        }

        /* Only sets event listener if dynamic-width is set */
        if (this.dynamicWidth && !window.BrowserDetection.isIE()) {
            this.addEventListener("dom-change", () => {
                this._setDropdownWidth();
            });
            this.resetDefaultWidth();
        }
        this._mapProperties();
    }

    /**
     * Sets language and country properties and prepares translations
     */
    _localize() {
        this.TRANSLATION_HELPER = new TranslationsUtil(this.language, this.country, Translations);
        this.language = this.TRANSLATION_HELPER.language;
        this.country = this.TRANSLATION_HELPER.country;
        if (!this.shouldAwaitTranslations) {
            this._assignTranslations();
        }
    }

    /**
     * Observer on serverTranslations that gathers all translations and maps them to properties.
     * Any strings set as properties and defined in the html attributes take precedence over translations from the server,
     * which in turn take precedence over the default translations in the translations file.
     */
    async _assignTranslations() {
        if (!this.TRANSLATION_HELPER) {
            this._localize();
        }
        if (this.webPageName) {
            this.serverTranslations = this.serverTranslations || await this.TRANSLATION_HELPER.getServerTranslations(this.webPageName);
        }
        let allTranslations = this.TRANSLATION_HELPER.getDefaultTranslations();
        if (this.serverTranslations) {
            allTranslations = Object.assign({}, allTranslations, this.serverTranslations);
        }
        Object.entries(allTranslations).forEach(([key, value]) => {
            this[key] = this[key] || value;
        });
        this.label = this.hideLabel ? '' : this.label;
    }

    _isMobile() {
        return window.innerWidth < 768;
    }

    _mapProperties() {
        if (this.optionsObjectArray && this.optionsObjectArray.length > 0) {
            this.options = [];
            this.subtitles = [];
            this.idArray = [];
            this.flairArray = [];
            if (this.titlePath.length > 0) {
                for (var i = 0; i < this.optionsObjectArray.length; i++) {
                    this.push('options', this.optionsObjectArray[i][this.titlePath]);
                }
            }
            if (this.subtitlePath.length > 0) {
                for (var i = 0; i < this.optionsObjectArray.length; i++) {
                    this.push('subtitles', this.optionsObjectArray[i][this.subtitlePath]);
                }
            }
            if (this.idPath.length > 0) {
                for (var i = 0; i < this.optionsObjectArray.length; i++) {
                    this.push('idArray', this.optionsObjectArray[i][this.idPath]);
                }
            }
            if (this.flairPath.length > 0) {
                for (var i = 0; i < this.optionsObjectArray.length; i++) {
                    this.push('flairArray', this.optionsObjectArray[i][this.flairPath]);
                }
            }
        }
        this._setOptions();
    }

    _setDropdownWidth() {
        /* Defensive if statement to bounce IE in case initial ready check is bypassed */
        if (window.BrowserDetection.isIE()) {
            return;
        }
        let dropdownElements = this.shadowRoot.querySelectorAll('.item-title');
        if (dropdownElements) {
            let longest = 0;
            for (let i = 0; i < dropdownElements.length; i++) {
                let thisEl = dropdownElements[i].querySelector('span');
                if (thisEl) {
                    if (thisEl.offsetWidth > longest) {
                        longest = thisEl.offsetWidth;
                    }
                }
            }
            let dropdownPadding = parseInt(getComputedStyle(this).getPropertyValue('--padding-value'), 10);
            if (this._isMobile()) {
                this.resetDefaultWidth();
            } else if (longest + (dropdownPadding * 2) < this._defaultDropdownMinWidth) {
                if (getComputedStyle(this).getPropertyValue('--minimum-dropdown-width') > this._defaultDropdownMinWidth) {
                    this.resetDefaultWidth();
                }
            } else {
                /* The padding value must be multiplied by 2 to get the full width of the element */
                this.updateStyles({
                    '--minimum-dropdown-width': longest + (dropdownPadding * 2) + 'px',
                    '--dropdown-wrap': 'nowrap'
                });
            }
        }
    }

    _setOptions() {
        if (this.optionsObjectArray && this.optionsObjectArray.length > 0) {
            var value;
            this.currentSelectionArr = [];
            this.optionsActive = [];
            if (this.options) {
                if (this.emptySelect) {
                }
                for (var i = 0; i < this.options.length; i++) {
                    if (!this.multiselect && i === this.selected) {
                        this.push('optionsActive', {
                            active: "active",
                            name: this.options[i],
                            subtitle: this.subtitles[i],
                            id: this.idArray[i],
                            flair: this.flairArray[i]
                        });
                    } else {
                        this.push('optionsActive', {
                            active: "inactive",
                            name: this.options[i],
                            subtitle: this.subtitles[i],
                            id: this.idArray[i],
                            flair: this.flairArray[i]
                        });
                    }
                }
            } else {
                console.log('No or improperly formatted options provided for ns-dropdown. Dropdown title: ', this.title);
            }
            if (!this.multiselect) {
                if (this.optionsObjectArray && this.optionsObjectArray.length > 0 && this.selected >= 0) {
                    if (this.useSubtitle) {
                        this.currentSelection = this.trim(this.optionsObjectArray[this.selected][this.subtitlePath]);
                    } else {
                        this.currentSelection = this.trim(this.optionsObjectArray[this.selected][this.titlePath]);
                    }
                } else if (this.selected >= 0) {
                    this.currentSelection = this.options[this.selected];
                }
            }
            if (this.useFlair && this.optionsObjectArray.length > 0) {
                this.flair = this.optionsObjectArray[this.selected][this.flairPath];
            }

            if (!this.selectionChanged) {
                this.origSelection = this.currentSelection ? this.currentSelection : this.selected;
            }
        }
        this._dropdownLayout();
    }

    _dropdownLayout() {
        if (this.options.length > 0 && this.optionsObjectArray.length > 0) {
            if (this.options.length < 2 || this.optionsObjectArray.length < 2) {
                if (this.hideDropdown) {
                    this.dropdownLayout = false;
                    return;
                }
                this.showSingleItemLayout = true;
                this.showDropdownLayout = false;
            }

            if (this.showDropdownLayout && !this.showSingleItemLayout) {
                this.showSingleItemLayout = false;
                this.showDropdownLayout = true;
            }
        }

        this.dropdownLayout = true;
    }

    close() {
        this.active = "inactive";
    }

    getSelection(e) {
        this.setSelection(e.model.index);
        if (this.useFlair && this.optionsObjectArray.length > 0) {
            this.flair = this.optionsObjectArray[e.model.index][this.flairPath]
        }
    }

    resetDefaultWidth() {
        if (this._isMobile()) {
            this.updateStyles({
                '--minimum-dropdown-width': this._defaultDropdownMinWidth + 'px',
                '--dropdown-wrap': 'default'
            });
        } else {
            this.updateStyles({
                '--minimum-dropdown-width': this._defaultDropdownMinWidth + 'px',
                '--dropdown-wrap': 'nowrap'
            });
        }
    }

    setSelection(index) {
        this.selectionChanged = true;
        if (!this.multiselect && this.selected === index) {
            return;
        }
        if (this.multiselect && this.get('optionsActive.' + index + '.active') === 'active') {
            this.set('optionsActive.' + index + '.active', 'inactive');
            if (Array.isArray(this.currentSelectionArr)) {
                this.splice('currentSelectionArr', this.currentSelectionArr.indexOf(this.optionsActive[index].name), 1);
            }
        } else {
            this.set('optionsActive.' + index + '.active', 'active');
            if (this.multiselect) {
                if (this.useSubtitle) {
                    this.push('currentSelectionArr', this.optionsActive[index].subtitle);
                } else {
                    this.push('currentSelectionArr', this.optionsActive[index].name);
                }
            } else {
                this.selected > -1 ? this.set('optionsActive.' + this.selected + '.active', 'inactive') : this.set('optionsActive.0.active', 'inactive');
                if (this.useSubtitle) {
                    this.set('currentSelectionArr', this.optionsActive[index].subtitle);
                } else {
                    this.set('currentSelectionArr', this.optionsActive[index].name);
                }
            }
        }
        this.set('currentSelection', this.trim(this.currentSelectionArr.toString()));
        if (!this.multiselect) {
            this.selected = index;
        }

        // Set dirty flag
        this.dirty = this.currentSelection !== this.origSelection;
    }

    /**
     * Observer for this.currentSelection. Assigns the trimmed version of the string to this.currentSelection. If the
     * string is already trimmed, or this.dontTrim is true, it will not change the string.
     */
    _trimCurrentSelection() {
        if (this.currentSelection) {
            this.set('currentSelection', this.trim(this.currentSelection.toString()));
        }
    }

    /**
     * Triggers validation on-blur.
     * Form validation - ns-form looks for this function for validation
     */
    showErrors() {
        this.el = this.shadowRoot.querySelector('.dropdown-wrap');
        this.errorEl = this.shadowRoot.querySelector('error-tag');
        if (this.el && this.errorEl) {
            if (this.invalid) {
                this.el.classList.add('error');
                this.el.classList.add('error-active');
                this.errorEl.style.display = 'block';
            } else {
                this.el.classList.remove('error');
                this.el.classList.remove('error-active');
                this.errorEl.style.display = 'none';
            }
        }
    }

    toggle() {
        this.active = this.active === "active" ? "inactive" : "active";
        this.validate();
    }

    toString() {
        return this.currentSelectionArr.length === 0 ? this.title : this.currentSelectionArr.join(', ');
    }

    trim(str) {
        if (!this.dontTrim) {
            return (str.length > this.trimLength) ? str.substring(0, this.trimLength) + '...' : str;
        } else {
            return str;
        }
    }

    open() {
        this.active = "active";
    }

    /**
     * Triggers validation on-blur.
     * Form validation - ns-form looks for this function for validation
     */
    validate() {
        let selectedVal = this.currentSelection;
        if (this.validationConditions(selectedVal)) {
            this.valid = false;
        } else {
            this.valid = true;
        }
        this.invalid = !this.valid;
        this.showErrors();
        return this.valid;
    }

    /**
     * Evaluates validation conditions to boolean
     * @param {*} selectedIndex
     * @param {*} selectedVal
     * @returns Boolean
     */
    validationConditions(selectedVal) {
        return (this.required && !selectedVal);
    }

}

customElements.define(NsDropdown.is, NsDropdown);
